import { computeSnap } from './geometry';
import { createGuideOverlay } from './guideOverlay';

/** The id fragments the editor already uses to recognise a draggable object. */
const OBJECT_ID_PARTS = ['image', 'shape', 'wordart', 'smartart', 'text', 'title', 'layout'];

export const isSlideObject = (element) =>
  Boolean(element.id) && OBJECT_ID_PARTS.some((part) => element.id.includes(part));

const INERT = { apply() {}, end() {} };

/** Measured geometry in pixels, with the slide's own top-left as the origin. */
const rectWithin = (element, rootRect) => {
  const rect = element.getBoundingClientRect();
  return {
    left: rect.left - rootRect.left,
    top: rect.top - rootRect.top,
    width: rect.width,
    height: rect.height,
  };
};

/**
 * Objects are positioned in percentages, so a correction has to be expressed as
 * one too. The element's own style is the basis where it has a usable one —
 * writing back a measured value would quietly change what a percentage width
 * means for any object with padding.
 */
const currentPercent = (styleValue, measuredPx, basisPx) => {
  const trimmed = (styleValue || '').trim();
  if (trimmed.endsWith('%')) {
    const parsed = parseFloat(trimmed);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return (measuredPx / basisPx) * 100;
};

/**
 * Runs for the length of one drag or resize. Targets are measured once up front,
 * since nothing but the active object can move while the mouse is down.
 */
export function beginGuideSession(activeEl) {
  const slideRoot = activeEl && activeEl.closest && activeEl.closest('.slide-container');
  if (!slideRoot) return INERT;

  const rootRect = slideRoot.getBoundingClientRect();
  const slide = { width: rootRect.width, height: rootRect.height };
  if (!slide.width || !slide.height) return INERT;

  const targets = [...slideRoot.children]
    .filter((child) => child !== activeEl && isSlideObject(child))
    .map((child) => ({ id: child.id, ...rectWithin(child, rootRect) }))
    .filter((rect) => rect.width > 0 && rect.height > 0);

  const overlay = createGuideOverlay(slideRoot);

  const nudge = (property, deltaPx, measuredPx, basisPx) => {
    if (!deltaPx) return;
    const base = currentPercent(activeEl.style[property], measuredPx, basisPx);
    activeEl.style[property] = `${base + (deltaPx / basisPx) * 100}%`;
  };

  const apply = ({ handle = null, altKey = false } = {}) => {
    const active = rectWithin(activeEl, rootRect);
    const { dx, dy, dw, dh, guides } = computeSnap(active, targets, slide, { handle, altKey });

    nudge('left', dx, active.left, slide.width);
    nudge('top', dy, active.top, slide.height);
    nudge('width', dw, active.width, slide.width);
    nudge('height', dh, active.height, slide.height);

    overlay.render(guides);
  };

  return { apply, end: overlay.destroy };
}
